// src/beta/components/AttractLoop.tsx
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getStory } from '@/beta/services/stories';
import { useReaderStore } from '@/beta/store/reader';
import { useShowcaseStore } from '@/beta/store/showcase';
import { useAutoAdvance } from '@/beta/hooks/useAutoAdvance';
import { Slide } from '@/beta/components/Slide';
import type { V1Slide } from '@/beta/types/story';

interface PhaseHero {
  phase: string;
  hero_image_url: string | null;
  image_count: number;
}

interface Props {
  primaryStoryId: string | null;
  /** From bundle.phases — used as the fallback "phase walk" when no story exists. */
  phases: PhaseHero[];
}

/** Synthesize an in-memory slide list from a phase array (fallback path).
 *  Phases with no hero image are skipped. */
function phasesToSlides(phases: PhaseHero[]): V1Slide[] {
  return phases
    .filter((p) => p.hero_image_url != null)
    .map((p) => ({
      id: `phase-${p.phase}`,
      type: 'image' as const,
      block: {
        id: `phase-${p.phase}`,
        blockType: 'image' as const,
        imageUrl: p.hero_image_url as string,
        caption: p.phase.replace(/-/g, ' '),
      },
      overlayTexts: [],
    }));
}

/** AttractLoop — Focus screen autoplay (spec § 6.5).
 *  Renders the primary story's slides full-bleed, auto-advancing every 6s.
 *  Falls back to a phase-walk when no story exists.
 *  On any pointer event, reverses direction (so wake-back never replays the
 *  same opening) and clears isAttract. */
export function AttractLoop({ primaryStoryId, phases }: Props) {
  const slideIndex = useReaderStore((s) => s.slideIndex);
  const setSlideIndex = useReaderStore((s) => s.setSlideIndex);
  const reverseDirection = useReaderStore((s) => s.reverseDirection);
  const setIsAttract = useShowcaseStore((s) => s.setIsAttract);

  const storyQ = useQuery({
    queryKey: ['beta', 'story', primaryStoryId],
    queryFn: () => getStory(primaryStoryId!),
    enabled: !!primaryStoryId,
    staleTime: 5 * 60 * 1000,
  });

  const slides: V1Slide[] = primaryStoryId
    ? storyQ.data?.slides ?? []
    : phasesToSlides(phases);

  const total = slides.length;

  // Keep a ref to total so the auto-advance callback reads the latest value
  // even when it fires multiple times before a React re-render.
  const totalRef = useRef(total);
  totalRef.current = total;

  // Auto-advance — wraps to 0 at end. Direction-aware so a wake-back that
  // reversed direction plays in reverse the next time the loop fires.
  // Read state directly from the store so rapid successive interval fires
  // (e.g., in tests) don't all use the same stale closure value.
  useAutoAdvance({
    onAdvance: () => {
      const { slideIndex: idx, direction: dir } = useReaderStore.getState();
      const t = totalRef.current;
      if (t === 0) return;
      const nextIdx =
        dir === 'forward'
          ? (idx + 1) % t
          : (idx - 1 + t) % t;
      setSlideIndex(nextIdx);
    },
    intervalMs: 6000,
    paused: total === 0,
  });

  // Wake-back: any pointer event reverses direction + exits attract.
  useEffect(() => {
    const wake = () => {
      reverseDirection();
      setIsAttract(false);
    };
    document.addEventListener('pointerdown', wake);
    return () => document.removeEventListener('pointerdown', wake);
  }, [reverseDirection, setIsAttract]);

  if (total === 0) return null;
  const slide = slides[Math.max(0, Math.min(slideIndex, total - 1))];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: '#000',
      }}
    >
      <div key={slide.id} className="vm-cross-dissolve-enter" style={{ width: '100%', height: '100%' }}>
        <Slide slide={slide} />
      </div>
    </div>
  );
}
